import { useEffect, useRef, useState } from "react";
import { EventMap } from "./EventMap";
import { EventModal } from "@/components/calendar/EventModal";
import { groupByVenue, groupIntoSeries } from "@/lib/series";
import type { Doc } from "convex/_generated/dataModel";

interface MapViewProps {
  events: Doc<"events">[];
  isLoading?: boolean;
  initialEvent?: Doc<"events">;
}

export function MapView({ events, isLoading, initialEvent }: MapViewProps) {
  const [selectedEvent, setSelectedEvent] = useState<Doc<"events"> | null>(null);
  const initialEventOpened = useRef(false);

  useEffect(() => {
    if (initialEvent && !initialEventOpened.current) {
      initialEventOpened.current = true;
      setSelectedEvent(initialEvent);
    }
  }, [initialEvent]);

  if (isLoading) {
    return (
      <div className="h-[500px] bg-secondary/30 rounded-lg animate-pulse flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading map...</p>
      </div>
    );
  }

  // Counted as venues and series rather than occurrences: the map draws one
  // pin per venue, and "178 of 199 events mapped" described neither.
  const venues = groupByVenue(events);
  const mappedSeries = groupIntoSeries(
    events.filter((e) => e.venueLatitude && e.venueLongitude)
  );

  return (
    <>
      <div className="relative">
        <EventMap
          events={events}
          onEventSelect={setSelectedEvent}
          height="calc(100vh - 200px)"
          className="min-h-[400px]"
        />
        <div className="absolute bottom-3 left-3 pointer-events-none">
          <div className="bg-background/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-muted-foreground shadow">
            {venues.length} venue{venues.length !== 1 ? "s" : ""} ·{" "}
            {mappedSeries.length} event{mappedSeries.length !== 1 ? "s" : ""}
          </div>
        </div>
        {venues.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-background/80 backdrop-blur-sm rounded-lg px-4 py-3 text-center shadow">
              <p className="text-muted-foreground text-sm">
                No events with location data yet
              </p>
              <p className="text-muted-foreground/60 text-xs mt-0.5">
                Run geocoding backfill in Admin → Tools
              </p>
            </div>
          </div>
        )}
      </div>

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </>
  );
}
